import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Shield, Scale, Building2, Briefcase, HardHat, Check, ArrowRight,
  Database, FileText, Code, Users, BarChart3, Zap
} from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";

const ForBusiness = () => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';
  const ahpDark = isAhp && isDark;

  const cream = ahpDark ? '#0a1628' : isAhp ? '#FAF9F6' : undefined;
  const ink = ahpDark ? '#f0ead6' : isAhp ? '#1B1B1B' : undefined;
  const navy = ahpDark ? '#7da6d4' : '#002868';
  const red = ahpDark ? '#e85d6c' : '#B22234';
  const gold = '#C5A55A';
  const cardBg = ahpDark ? '#111f36' : isAhp ? '#FFFFFF' : undefined;
  const border = ahpDark ? 'rgba(197,165,90,0.2)' : isAhp ? '#e5e0d5' : undefined;
  const serif = "'Instrument Serif', Georgia, serif";
  const sans = "'Instrument Sans', system-ui, sans-serif";

  const audiences = [
    { icon: <Shield className="w-6 h-6" />, label: "Medical Bill Advocates", link: "/blog/medical-bill-advocates-hospital-pricing-data" },
    { icon: <Scale className="w-6 h-6" />, label: "Personal Injury Attorneys", link: "/blog/personal-injury-attorneys-medical-cost-data" },
    { icon: <Building2 className="w-6 h-6" />, label: "Self-Funded Employers", link: "/blog/employer-healthcare-cost-benchmarks" },
    { icon: <Briefcase className="w-6 h-6" />, label: "Insurance Brokers", link: "/blog/insurance-broker-hospital-pricing-tools" },
    { icon: <HardHat className="w-6 h-6" />, label: "Workers' Comp", link: "/blog/workers-comp-medical-cost-benchmarks" },
  ];

  const plans = [
    {
      name: "Per Report",
      price: "$49",
      period: "per report",
      description: "For occasional use. Generate individual pricing reports as needed.",
      features: [
        "Single hospital pricing analysis",
        "National & regional benchmarks",
        "Procedure-level cost comparison",
        "PDF export",
        "No subscription required",
      ],
      highlighted: false,
      cta: "Generate a Report",
    },
    {
      name: "Professional",
      price: "$299",
      period: "/month",
      description: "For full-time advocates, attorneys, and consultants handling multiple cases.",
      features: [
        "Unlimited report generation",
        "Bulk hospital comparisons",
        "White-label PDF exports",
        "Scenario-based cost modeling",
        "API access (1,000 calls/day)",
        "Priority support",
      ],
      highlighted: true,
      cta: "Start Professional Plan",
    },
    {
      name: "Enterprise",
      price: "$499",
      period: "/month",
      description: "Full programmatic access for integration with your existing systems.",
      features: [
        "Everything in Professional",
        "Unlimited API access",
        "Bulk data exports",
        "Custom integrations support",
        "Embeddable widgets",
        "Dedicated support",
      ],
      highlighted: false,
      cta: "Start Enterprise Plan",
    },
  ];

  const stats = [
    { value: "9,500+", label: "Hospitals tracked" },
    { value: "63,000+", label: "Procedure codes" },
    { value: "555M+", label: "Price data points" },
    { value: "50 States", label: "+ DC coverage" },
  ];

  const dataFeatures = [
    { icon: <Database className="w-5 h-5" />, title: "Complete Chargemaster Data", desc: "Gross charges from every hospital's price transparency filing" },
    { icon: <BarChart3 className="w-5 h-5" />, title: "National Benchmarks", desc: "Median, percentile, and average pricing across all facilities" },
    { icon: <FileText className="w-5 h-5" />, title: "85+ Medical Scenarios", desc: "Phase-by-phase cost modeling for common treatment pathways" },
    { icon: <Zap className="w-5 h-5" />, title: "Quality Metrics", desc: "CMS ratings, mortality, readmission, and patient experience scores" },
    { icon: <Code className="w-5 h-5" />, title: "REST API", desc: "Programmatic access to all data for custom integrations" },
    { icon: <Users className="w-5 h-5" />, title: "International Comparisons", desc: "US pricing vs. Australia, France, Canada, and UK" },
  ];

  return (
    <>
      <Helmet>
        <title>Hospital Pricing Data for Business | {brand.name}</title>
        <meta name="description" content="Access hospital pricing data from 9,500+ facilities. Self-service plans for medical bill advocates, attorneys, employers, brokers, and workers' comp professionals." />
        <meta name="keywords" content="hospital pricing data subscription, healthcare cost data API, medical billing data access, hospital chargemaster data business, reasonable customary charges database" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/for-business`} />
        <meta property="og:title" content={`Hospital Pricing Data for Business | ${brand.name}`} />
        <meta property="og:description" content="Self-service access to pricing data from 9,500+ hospitals. No sales call required." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/for-business`} />
        <meta property="og:site_name" content={brand.name} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": `${brand.name} Business Plans`,
          "description": "Hospital pricing data access for professionals",
          "url": `${brand.primaryDomain}/for-business`,
          "brand": { "@type": "Organization", "name": brand.name },
          "offers": plans.map(p => ({
            "@type": "Offer",
            "name": p.name,
            "price": p.price.replace('$', ''),
            "priceCurrency": "USD"
          }))
        })}</script>
      </Helmet>

      <div className={isAhp ? "min-h-screen pb-24" : "min-h-screen bg-background pb-24"} style={isAhp ? { backgroundColor: cream, color: ink } : undefined}>

        {/* Hero */}
        <div
          className="py-16 md:py-24 px-4"
          style={isAhp ? {
            background: ahpDark
              ? 'linear-gradient(135deg, #0a1628 0%, #112244 50%, #0a1628 100%)'
              : 'linear-gradient(135deg, #002868 0%, #001a44 50%, #002868 100%)',
          } : { background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)/0.8) 100%)' }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white"
              style={isAhp ? { fontFamily: serif } : undefined}
            >
              Hospital Pricing Data
              <br />
              <span style={{ color: gold }}>For Your Business</span>
            </h1>
            <p
              className="text-lg md:text-xl mb-8 max-w-2xl mx-auto"
              style={{ color: 'rgba(255,255,255,0.8)', fontFamily: isAhp ? sans : undefined }}
            >
              Self-service access to pricing from 9,500+ hospitals. Generate reports, benchmark charges, and prove reasonable costs. No sales call required.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mt-12">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-white" style={isAhp ? { fontFamily: serif } : undefined}>
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: isAhp ? sans : undefined }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Who It's For */}
        <div className="max-w-4xl mx-auto px-4 py-16">
          <h2
            className={isAhp ? "text-3xl font-bold text-center mb-10" : "text-3xl font-bold text-center mb-10 text-foreground"}
            style={isAhp ? { fontFamily: serif, color: ink } : undefined}
          >
            Built For Professionals Who Fight Overcharges
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {audiences.map((a) => (
              <Link key={a.label} to={a.link} className="group">
                <div
                  className={isAhp ? "flex flex-col items-center p-4 rounded-lg text-center transition-all hover:shadow-md" : "flex flex-col items-center p-4 rounded-lg text-center transition-all hover:shadow-md bg-card border hover:border-primary/30"}
                  style={isAhp ? { backgroundColor: cardBg, border: `1px solid ${border}` } : undefined}
                >
                  <div
                    className="mb-2 p-2 rounded-full"
                    style={isAhp ? { color: navy, backgroundColor: ahpDark ? 'rgba(197,165,90,0.1)' : 'rgba(0,40,104,0.08)' } : undefined}
                  >
                    {a.icon}
                  </div>
                  <span
                    className="text-xs md:text-sm font-medium"
                    style={isAhp ? { fontFamily: sans, color: ink } : undefined}
                  >
                    {a.label}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div className="max-w-5xl mx-auto px-4 py-8">
          <h2
            className={isAhp ? "text-3xl font-bold text-center mb-4" : "text-3xl font-bold text-center mb-4 text-foreground"}
            style={isAhp ? { fontFamily: serif, color: ink } : undefined}
          >
            Simple, Self-Service Pricing
          </h2>
          <p
            className={isAhp ? "text-center mb-10 text-lg" : "text-center mb-10 text-lg text-muted-foreground"}
            style={isAhp ? { fontFamily: sans, color: ahpDark ? '#aaa' : '#666' } : undefined}
          >
            No contracts. No sales calls. Start generating reports today.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card
                key={plan.name}
                className={`relative ${plan.highlighted ? 'ring-2' : ''} ${isAhp ? '' : plan.highlighted ? 'ring-primary' : ''}`}
                style={isAhp ? {
                  backgroundColor: cardBg,
                  border: plan.highlighted ? `2px solid ${gold}` : `1px solid ${border}`,
                  ...(plan.highlighted ? { ringColor: gold } : {})
                } : undefined}
              >
                {plan.highlighted && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: isAhp ? gold : undefined, fontFamily: isAhp ? sans : undefined }}
                  >
                    MOST POPULAR
                  </div>
                )}
                <CardContent className="p-6 md:p-8">
                  <h3
                    className={isAhp ? "text-xl font-bold mb-1" : "text-xl font-bold mb-1 text-foreground"}
                    style={isAhp ? { fontFamily: serif, color: ink } : undefined}
                  >
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span
                      className="text-4xl font-bold"
                      style={isAhp ? { fontFamily: serif, color: navy } : undefined}
                    >
                      {plan.price}
                    </span>
                    <span
                      className="text-sm"
                      style={isAhp ? { fontFamily: sans, color: ahpDark ? '#888' : '#999' } : { color: '#999' }}
                    >
                      {plan.period}
                    </span>
                  </div>
                  <p
                    className={isAhp ? "text-sm mb-6" : "text-sm mb-6 text-muted-foreground"}
                    style={isAhp ? { fontFamily: sans, color: ahpDark ? '#aaa' : '#666' } : undefined}
                  >
                    {plan.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm" style={isAhp ? { fontFamily: sans, color: ahpDark ? '#ccc' : '#444' } : undefined}>
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: isAhp ? (ahpDark ? '#6bc9a0' : '#2b5e49') : undefined }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${isAhp ? 'text-white font-semibold' : ''}`}
                    variant={plan.highlighted ? "default" : "outline"}
                    style={isAhp ? (plan.highlighted
                      ? { backgroundColor: navy, fontFamily: sans }
                      : { borderColor: navy, color: navy, fontFamily: sans }
                    ) : undefined}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* What's In The Data */}
        <div className="max-w-4xl mx-auto px-4 py-16">
          <h2
            className={isAhp ? "text-3xl font-bold text-center mb-10" : "text-3xl font-bold text-center mb-10 text-foreground"}
            style={isAhp ? { fontFamily: serif, color: ink } : undefined}
          >
            What's In The Data
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataFeatures.map((f) => (
              <div
                key={f.title}
                className={isAhp ? "p-5 rounded-lg" : "p-5 rounded-lg bg-card border"}
                style={isAhp ? { backgroundColor: cardBg, border: `1px solid ${border}` } : undefined}
              >
                <div className="mb-3" style={isAhp ? { color: navy } : undefined}>
                  {f.icon}
                </div>
                <h3
                  className={isAhp ? "font-bold mb-1" : "font-bold mb-1 text-foreground"}
                  style={isAhp ? { fontFamily: serif, color: ink } : undefined}
                >
                  {f.title}
                </h3>
                <p
                  className="text-sm"
                  style={isAhp ? { fontFamily: sans, color: ahpDark ? '#aaa' : '#666' } : undefined}
                >
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto px-4 py-16">
          <h2
            className={isAhp ? "text-3xl font-bold text-center mb-10" : "text-3xl font-bold text-center mb-10 text-foreground"}
            style={isAhp ? { fontFamily: serif, color: ink } : undefined}
          >
            Common Questions
          </h2>
          {[
            {
              q: "Where does the pricing data come from?",
              a: "All data comes from hospital price transparency filings required under federal law (45 CFR 180). Hospitals are required to publish their standard charges in machine-readable format. We process these files from 9,500+ hospitals and normalize them into a comparable format."
            },
            {
              q: "How current is the data?",
              a: "Hospitals are required to update their price transparency files annually. We continuously process new filings as they become available. Most data reflects filings from the current or prior year."
            },
            {
              q: "Can I use this data in court?",
              a: "The underlying data is publicly available under federal transparency requirements, making it straightforward to authenticate. Our reports include source documentation and methodology statements. While our reports can support expert testimony, consult with your legal team about admissibility requirements in your jurisdiction."
            },
            {
              q: "Do I need a sales call to get started?",
              a: "No. All plans are self-service. Sign up, pay, and start generating reports immediately. If you need a custom arrangement or have questions, you can reach us via email."
            },
            {
              q: "What procedure codes are covered?",
              a: "Our database includes CPT, HCPCS, MS-DRG, APR-DRG, Revenue, NDC (drug), and supply codes. We cover 63,000+ unique procedure codes with national pricing averages."
            },
            {
              q: "Can I try before subscribing?",
              a: "Yes. Search any hospital on our platform for free to see their pricing profile, letter grade, and markup percentage. The public data gives you a clear sense of what's available in the paid reports."
            },
          ].map((faq, i) => (
            <div
              key={i}
              className={isAhp ? "py-5" : "py-5"}
              style={i > 0 ? { borderTop: `1px solid ${isAhp ? border : 'hsl(var(--border))'}` } : undefined}
            >
              <h3
                className={isAhp ? "font-bold mb-2" : "font-bold mb-2 text-foreground"}
                style={isAhp ? { fontFamily: serif, color: ink } : undefined}
              >
                {faq.q}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={isAhp ? { fontFamily: sans, color: ahpDark ? '#bbb' : '#555' } : undefined}
              >
                {faq.a}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="max-w-4xl mx-auto px-4 pb-16">
          <div
            className="p-8 md:p-12 rounded-xl text-center"
            style={isAhp ? {
              background: ahpDark
                ? 'linear-gradient(135deg, #112244, #0a1628)'
                : 'linear-gradient(135deg, #002868, #001a44)',
            } : { background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary)/0.8))' }}
          >
            <h2
              className="text-3xl font-bold mb-4 text-white"
              style={isAhp ? { fontFamily: serif } : undefined}
            >
              Start Using Hospital Pricing Data Today
            </h2>
            <p
              className="mb-8 max-w-xl mx-auto"
              style={{ color: 'rgba(255,255,255,0.8)', fontFamily: isAhp ? sans : undefined }}
            >
              Search any hospital for free. When you're ready for full access, pick a plan and start generating reports in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/search">
                <Button
                  size="lg"
                  className="font-semibold px-8"
                  style={isAhp ? { backgroundColor: gold, color: '#1B1B1B', fontFamily: sans } : undefined}
                >
                  Search Hospitals Free
                </Button>
              </Link>
              <Link to="/blog">
                <Button
                  size="lg"
                  variant="outline"
                  className="font-semibold px-8"
                  style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white', fontFamily: isAhp ? sans : undefined }}
                >
                  Read Industry Guides <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForBusiness;
